// Synthesized WebAudio SFX for Cities of Bharat — zero audio assets, everything
// generated with oscillators + filtered noise. The context is created lazily
// on first user gesture (autoplay policies); all one-shots are throttled so
// mass orders and battle ticks can't stack into noise.

interface ToneOpts {
  f: number;
  f2?: number;
  dur: number;
  type?: OscillatorType;
  gain?: number;
  delay?: number;
}

class SoundManager {
  muted: boolean;
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private last: Record<string, number> = {};

  constructor() {
    this.muted =
      typeof localStorage !== 'undefined' && localStorage.getItem('bharat-muted') === '1';
    if (typeof window !== 'undefined') {
      window.addEventListener('pointerdown', () => this.ensure(), { passive: true });
      window.addEventListener('keydown', () => this.ensure());
    }
  }

  toggleMute(): boolean {
    this.muted = !this.muted;
    try {
      localStorage.setItem('bharat-muted', this.muted ? '1' : '0');
    } catch {
      /* private mode etc: stay in memory */
    }
    if (this.master) this.master.gain.value = this.muted ? 0 : 0.35;
    return this.muted;
  }

  private ensure(): AudioContext | null {
    if (typeof window === 'undefined') return null;
    if (!this.ctx) {
      const AC =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!AC) return null;
      this.ctx = new AC();
      this.master = this.ctx.createGain();
      this.master.gain.value = this.muted ? 0 : 0.35;
      this.master.connect(this.ctx.destination);
    }
    if (this.ctx.state === 'suspended') void this.ctx.resume();
    return this.ctx;
  }

  private gate(key: string, ms: number): boolean {
    const now = performance.now();
    if (now - (this.last[key] ?? 0) < ms) return false;
    this.last[key] = now;
    return true;
  }

  private tone(o: ToneOpts): void {
    const ctx = this.ensure();
    if (!ctx || !this.master || this.muted) return;
    const t0 = ctx.currentTime + (o.delay ?? 0);
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = o.type ?? 'sine';
    osc.frequency.setValueAtTime(Math.max(o.f, 1), t0);
    if (o.f2) osc.frequency.exponentialRampToValueAtTime(Math.max(o.f2, 1), t0 + o.dur);
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(o.gain ?? 0.5, t0 + 0.008);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + o.dur);
    osc.connect(g);
    g.connect(this.master);
    osc.start(t0);
    osc.stop(t0 + o.dur + 0.05);
  }

  private noise(dur: number, freq: number, gain = 0.4, delay = 0): void {
    const ctx = this.ensure();
    if (!ctx || !this.master || this.muted) return;
    const t0 = ctx.currentTime + delay;
    const len = Math.max(1, Math.floor(ctx.sampleRate * dur));
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / len);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const flt = ctx.createBiquadFilter();
    flt.type = 'lowpass';
    flt.frequency.value = freq;
    const g = ctx.createGain();
    g.gain.value = gain;
    src.connect(flt);
    flt.connect(g);
    g.connect(this.master);
    src.start(t0);
  }

  // ---- public one-shots ----
  click(): void {
    this.tone({ f: 660, dur: 0.06, type: 'square', gain: 0.16 });
  }
  select(): void {
    if (!this.gate('select', 90)) return;
    this.tone({ f: 520, dur: 0.07, gain: 0.28 });
    this.tone({ f: 780, dur: 0.07, gain: 0.22, delay: 0.06 });
  }
  move(): void {
    if (!this.gate('move', 140)) return;
    this.tone({ f: 440, f2: 560, dur: 0.09, gain: 0.28 });
  }
  gather(): void {
    if (!this.gate('gather', 200)) return;
    this.noise(0.09, 1800, 0.32);
    this.tone({ f: 300, dur: 0.08, type: 'triangle', gain: 0.28 });
  }
  attack(): void {
    if (!this.gate('attack', 200)) return;
    this.tone({ f: 190, f2: 90, dur: 0.2, type: 'sawtooth', gain: 0.32 });
    this.noise(0.12, 900, 0.28);
  }
  build(): void {
    if (!this.gate('build', 200)) return;
    this.tone({ f: 130, f2: 60, dur: 0.16, gain: 0.5 });
    this.noise(0.1, 500, 0.3);
  }
  train(): void {
    if (!this.gate('train', 150)) return;
    this.tone({ f: 880, dur: 0.06, gain: 0.22 });
  }
  emerge(): void {
    if (!this.gate('emerge', 600)) return;
    this.tone({ f: 700, f2: 940, dur: 0.07, gain: 0.2 });
  }
  complete(): void {
    if (!this.gate('complete', 500)) return;
    [523, 659, 784].forEach((f, i) => this.tone({ f, dur: 0.16, gain: 0.28, delay: i * 0.09 }));
  }
  error(): void {
    if (!this.gate('error', 300)) return;
    this.tone({ f: 160, dur: 0.18, type: 'square', gain: 0.22 });
  }
  start(): void {
    this.tone({ f: 196, dur: 0.5, type: 'sawtooth', gain: 0.28 });
    this.tone({ f: 147, dur: 0.6, type: 'sawtooth', gain: 0.28, delay: 0.05 });
  }
  win(): void {
    [523, 659, 784, 1046].forEach((f, i) =>
      this.tone({ f, dur: 0.22, type: 'triangle', gain: 0.32, delay: i * 0.13 }),
    );
  }
  lose(): void {
    [392, 311, 233].forEach((f, i) =>
      this.tone({ f, dur: 0.3, type: 'triangle', gain: 0.32, delay: i * 0.18 }),
    );
  }
}

export const sfx = new SoundManager();
